import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  buildPatientDirectoryEventProps,
  buildPatientSharedEventProps,
  GET_PATIENT_PROFILES,
  resolveIsCrossClinicianLink,
  type PatientDirectoryEventProps,
  type PatientSharedEventProps,
} from '@heidi/patients/contracts';
import {
  GET_SESSION_BY_ID,
  sessionKeys,
  type SessionResponse,
  type SessionTeamContext,
} from '@heidi/session/contracts';
import { GET_USER, type UserResponse } from '@heidi/workspace/contracts';

/**
 * Emit-time resolver for the shared-directory properties on the Part 1
 * `patient_*` events (COLAB-422).
 *
 * Emit sites run in mutation callbacks and effects, so none of this can be a
 * hook. The three sources are read synchronously instead:
 *
 * - the directory gate from a module-level snapshot that
 *   `usePatientDirectoryAnalyticsSync` keeps current (the gate needs a flag
 *   hook, so it cannot be read from a cache);
 * - the actor from the cached current user;
 * - the profile's manager from the caller's profile, else the patient-profile
 *   caches (the by-id entry, then any cached list page holding the row).
 *
 * Every read fails closed: a cold cache or an unresolved gate omits the
 * property, and the pure builder in `@heidi/patients/contracts` decides what
 * that means.
 */

// Read from the cache, not through `@/hooks/query/useQueryUser`: that module
// pulls the flags barrel and half the app, and this one sits under every patient
// emit host, so a heavy import here breaks any suite that loads a host.
const getCurrentUser = () => reactQueryClient.getQueryData<UserResponse>([GET_USER]);

let directoryGate: boolean | undefined;

/** Written by `usePatientDirectoryAnalyticsSync`. `undefined` means unresolved. */
export function setPatientDirectoryGate(gate: boolean | undefined): void {
  directoryGate = gate;
}

export function getPatientDirectoryGate(): boolean | undefined {
  return directoryGate;
}

/** What the caller already knows about the patient. */
export type PatientSharedProfileSummary = {
  /** Absent on a profile shape that predates COLAB-411: the cache then fills in. */
  managed_by_user_ref_id?: string | null;
  // Any profile shape may be passed. Without this a `PatientProfile`, which
  // does not carry the field above, fails the weak-type check.
  [key: string]: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The profile row for `patientId` inside one cached `GET_PATIENT_PROFILES`
 * entry. The family holds three shapes: a bare array (client-paginated list), a
 * `{ patients: [...] }` page (server-paginated list) and a single profile (the
 * by-id read).
 */
const findProfileRow = (
  data: unknown,
  patientId: string
): PatientSharedProfileSummary | undefined => {
  if (Array.isArray(data)) {
    return (data as unknown[]).find(
      (row): row is PatientSharedProfileSummary => isRecord(row) && row.id === patientId
    );
  }
  if (!isRecord(data)) return undefined;
  if (Array.isArray(data.patients)) return findProfileRow(data.patients, patientId);
  return data.id === patientId ? data : undefined;
};

/**
 * The manager ref the patient-profile caches know for this patient: the by-id
 * entry first, then any cached list page that holds the row. A drawer opened
 * from a list row has no by-id entry yet, but the row it was opened from
 * carries the manager ref. The first source with a value wins, so a `null`
 * manager falls through to the next source or is omitted.
 */
const readCachedManagerRef = (
  patientId: string | null | undefined
): string | undefined => {
  if (!patientId) return undefined;
  const byId = reactQueryClient.getQueryData<PatientSharedProfileSummary>([
    GET_PATIENT_PROFILES,
    { patientProfileId: patientId },
  ]);
  const rows = [
    byId,
    ...reactQueryClient
      .getQueriesData<unknown>({ queryKey: [GET_PATIENT_PROFILES] })
      .map(([, data]) => findProfileRow(data, patientId)),
  ];
  return (
    rows.find((row) => row?.managed_by_user_ref_id != null)?.managed_by_user_ref_id ??
    undefined
  );
};

/**
 * The directory state alone, for an event with no patient
 * (`patient_list_viewed`, `patient_searched`). `patient_is_own` cannot apply to
 * a whole list or a query, so it is not part of the return type.
 */
export function resolvePatientDirectoryEventProps(): PatientDirectoryEventProps {
  return buildPatientDirectoryEventProps(directoryGate);
}

/**
 * The shared properties for an event about one patient: the directory state
 * and whether the patient is the actor's own.
 */
export function resolvePatientSharedEventProps(input: {
  patientId?: string | null;
  profile?: PatientSharedProfileSummary | null;
}): PatientSharedEventProps {
  // Caller first: a profile shape that predates COLAB-411 carries no manager,
  // and the caches may still know it.
  return buildPatientSharedEventProps({
    directoryEnabled: directoryGate,
    actorUserRefId: getCurrentUser()?.user_ref_id,
    managedByUserRefId:
      input.profile?.managed_by_user_ref_id ?? readCachedManagerRef(input.patientId),
  });
}

/**
 * `is_cross_clinician_link` for a link or unlink over these sessions.
 *
 * The cached session carries its owner as `kinde_gp_id`, so the actor is
 * compared by `kinde_user_id`: same id space on both sides. A team-member
 * (review mode) session is cached under the member-scoped key, so pass the team
 * context there and the owner is read from that entry first, then from the plain
 * by-id entry. A session in neither cache has an unknown owner.
 */
export function resolveSessionsCrossClinicianLink(
  sessionIds: readonly string[],
  teamContext?: SessionTeamContext
): boolean | null {
  const readOwner = (sessionId: string) =>
    (
      reactQueryClient.getQueryData<SessionResponse>(
        sessionKeys.byId(sessionId, teamContext)
      ) ??
      reactQueryClient.getQueryData<SessionResponse>([GET_SESSION_BY_ID, { sessionId }])
    )?.kinde_gp_id;
  return resolveIsCrossClinicianLink(
    getCurrentUser()?.kinde_user_id,
    sessionIds.map(readOwner)
  );
}

/**
 * The payload for an event about one patient with the shared properties merged
 * under it. A property the caller already set wins.
 */
export function withPatientSharedProps<T extends { patient_id: string }>(
  input: T,
  profile?: PatientSharedProfileSummary | null
): T & PatientSharedEventProps {
  return {
    ...resolvePatientSharedEventProps({ patientId: input.patient_id, profile }),
    ...input,
  };
}
